import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Plus, 
  Users, 
  Calendar, 
  Copy,
  MapPin, 
  Clock, 
  ChevronRight,
  UserMinus,
  UserCheck,
  UserPlus,
  XCircle,
  CheckCircle,
  AlertCircle,
  Share2,
  Send,
  Loader2,
  X,
  Lock,
  Users2,
} from 'lucide-react';
import Footer from '../components/Footer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { isPastRun } from '../utils/datetime';
import HostSchedule from '../components/HostSchedule';
import HostPlayerManagementDialog from '../components/HostPlayerManagementDialog';
import HostPlayerMessageDialog from '../components/HostPlayerMessageDialog';
import HostGameCancellationDialog from '../components/HostGameCancellationDialog';
import ShareRunSheet from '../components/ShareRunSheet';
import HostDashboardHeader from '../components/HostDashboardHeader';
import { getDuplicateGameSource } from '../utils/duplicateGame';
import { invitationPlayerLabel } from '../utils/invitationDisplay';

const HOST_VIEW_STORAGE_KEY = 'openruns.hostDashboard.view';

const HostDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [runs, setRuns] = useState({ upcoming: [], past: [] });
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubsLoading, setClubsLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [gameToCancel, setGameToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [dashboardView, setDashboardView] = useState(() => {
    try {
      if (typeof window === 'undefined') return 'schedule';
      return window.localStorage.getItem(HOST_VIEW_STORAGE_KEY) === 'list' ? 'list' : 'schedule';
    } catch {
      return 'schedule';
    }
  });
  
  // Invite system state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [invitablePlayers, setInvitablePlayers] = useState([]);
  const [loadingInvitablePlayers, setLoadingInvitablePlayers] = useState(false);
  const [invitingPlayer, setInvitingPlayer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [shareRun, setShareRun] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [playersGame, setPlayersGame] = useState(null);
  const [playersOpen, setPlayersOpen] = useState(false);
  const [messageGame, setMessageGame] = useState(null);
  const [messageOpen, setMessageOpen] = useState(false);

  useEffect(() => {
    fetchRuns();
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      setClubsLoading(true);
      const response = await apiClient.get('/clubs/mine');
      setClubs(response.data.clubs || []);
    } catch (error) {
      console.error('Failed to load clubs:', error);
      setClubs([]);
    } finally {
      setClubsLoading(false);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await apiClient.get('/host/my-runs');
      setRuns(response.data);
    } catch (error) {
      toast.error('Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlist = async (gameId) => {
    try {
      const response = await apiClient.get(`/games/${gameId}/waitlist`);
      setWaitlist(response.data.waitlist || []);
    } catch (error) {
      setWaitlist([]);
    }
  };

  const handleSelectGame = async (game) => {
    setSelectedGame(game);
    await fetchWaitlist(game.id);
  };

  const handleDuplicateGame = (game) => {
    navigate('/create-game', {
      state: { cloneFrom: getDuplicateGameSource(game) },
    });
  };

  const handleRemovePlayer = async (gameId, playerId, playerName) => {
    if (!confirm(`Remove ${playerName} from this game?`)) return false;
    
    try {
      await apiClient.post(`/games/${gameId}/host/remove-player/${playerId}`);
      toast.success(`${playerName} removed from game`);
      fetchRuns();
      if (selectedGame?.id === gameId) {
        fetchWaitlist(gameId);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove player');
      return false;
    }
  };

  const handlePromoteFromWaitlist = async (gameId, playerId, playerName) => {
    try {
      await apiClient.post(`/games/${gameId}/host/promote-waitlist/${playerId}`);
      toast.success(`${playerName} promoted from waitlist`);
      fetchRuns();
      fetchWaitlist(gameId);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote player');
      return false;
    }
  };

  const handleCancelGame = (game) => {
    setGameToCancel(game);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const confirmCancelGame = async () => {
    if (!gameToCancel) return;
    
    try {
      setIsCancelling(true);
      await apiClient.post(`/games/${gameToCancel.id}/host/cancel`, {
        message: cancelReason || null,
      });
      toast.success('Game cancelled');
      await fetchRuns();
      setSelectedGame(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel game');
    } finally {
      setIsCancelling(false);
      setCancelDialogOpen(false);
      setGameToCancel(null);
      setCancelReason('');
    }
  };

  const getStatusBadge = (status, playerCount, minPlayers) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Confirmed
          </span>
        );
      case 'needs_players':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
            <AlertCircle className="w-3 h-3" />
            Needs {minPlayers - playerCount} more
          </span>
        );
      case 'full':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            <Users className="w-3 h-3" />
            Full
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            Open
          </span>
        );
    }
  };

  const formatDateTime = (dateStr, tz) => {
    if (!dateStr) return { date: 'Date TBD', time: '' };
    try {
      // Handle dates with or without timezone suffix
      const str = String(dateStr);
      let date;
      if (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str)) {
        date = new Date(str);
      } else {
        date = new Date(str + 'Z');
      }
      
      if (isNaN(date.getTime())) return { date: 'Date TBD', time: '' };
      
      // Use user's timezone (not game's) for consistency
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return {
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: timezone }),
        time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone })
      };
    } catch {
      return { date: 'Date TBD', time: '' };
    }
  };

  // === Invite System Functions ===
  
  const openInviteModal = async (game) => {
    if (!game) return;
    setSelectedGame(game);
    setInviteModalOpen(true);
    setLoadingInvitablePlayers(true);
    setSearchQuery('');
    
    try {
      const response = await apiClient.get(`/games/${game.id}/invitable-players`);
      setInvitablePlayers(response.data.players || []);
    } catch (error) {
      toast.error('Failed to load invitable players');
      setInvitablePlayers([]);
    } finally {
      setLoadingInvitablePlayers(false);
    }
  };
  
  const handleInvitePlayer = async (playerId) => {
    if (!selectedGame) return;
    
    setInvitingPlayer(playerId);
    try {
      const response = await apiClient.post(`/games/${selectedGame.id}/invite`, {
        invited_user_id: playerId
      });
      
      if (response.data.already_invited) {
        toast.info('Player already invited');
      } else {
        toast.success('Invite sent!');
      }
      
      // Update local state to mark as invited
      setInvitablePlayers(prev => 
        prev.map(p => p.id === playerId ? { ...p, already_invited: true } : p)
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInvitingPlayer(null);
    }
  };
  
  const openShareSheet = (game) => {
    if (!game) return;
    setShareRun(game);
    setShareOpen(true);
  };

  const openPlayerManagement = (game) => {
    if (!game) return;
    setPlayersGame(game);
    setPlayersOpen(true);
  };

  const openPlayerMessage = (game) => {
    if (!game) return;
    setMessageGame(game);
    setMessageOpen(true);
  };
  
  const filteredPlayers = invitablePlayers.filter(p => 
    invitationPlayerLabel(p).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewChange = (view) => {
    setDashboardView(view);
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(HOST_VIEW_STORAGE_KEY, view);
    } catch {
      // Local persistence is helpful, but the dashboard should still work without it.
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading your runs...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-24">
      <HostDashboardHeader onNavigate={navigate} />

      <div className="flex-1 max-w-6xl mx-auto w-full px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <p className="text-2xl font-bold text-gray-900">{runs.upcoming.length}</p>
            <p className="text-sm text-gray-500">Upcoming Runs</p>
          </Card>
          <Card className="p-4">
            <p className="text-2xl font-bold text-gray-900">{runs.past.length}</p>
            <p className="text-sm text-gray-500">Past Runs</p>
          </Card>
          <Card className="p-4">
            <p className="text-2xl font-bold text-gray-900">{runs.total_runs}</p>
            <p className="text-sm text-gray-500">Total Runs</p>
          </Card>
          <Card className="p-4">
            <p className="text-2xl font-bold text-gray-900">{runs.total_players_hosted}</p>
            <p className="text-sm text-gray-500">Players Hosted</p>
          </Card>
        </div>

        <div className="mb-6 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Schedule</h2>
          <div
            className="inline-flex rounded-lg border border-zinc-200 bg-white p-1"
            role="tablist"
            aria-label="Host dashboard view"
            data-testid="host-dashboard-view-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={dashboardView === 'schedule'}
              onClick={() => handleViewChange('schedule')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                dashboardView === 'schedule'
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'text-zinc-600 hover:bg-zinc-50'
              }`}
              data-testid="host-schedule-view-btn"
            >
              Schedule
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={dashboardView === 'list'}
              onClick={() => handleViewChange('list')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                dashboardView === 'list'
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'text-zinc-600 hover:bg-zinc-50'
              }`}
              data-testid="host-list-view-btn"
            >
              List
            </button>
          </div>
        </div>

        {dashboardView === 'schedule' ? (
          <HostSchedule
            runs={runs}
            clubs={clubs}
            onOpenSession={(session) => navigate(`/games/${session.id}`)}
            onCreateSession={() => navigate('/create-game')}
            onShareSession={openShareSheet}
            onInviteSession={openInviteModal}
            onViewPlayers={openPlayerManagement}
            onCheckInSession={openPlayerManagement}
            onDuplicateSession={handleDuplicateGame}
            onCancelSession={handleCancelGame}
          />
        ) : (
          <>

        {/* My Clubs Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">My Clubs</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/create-club')}
              className="border-orange-200 text-orange-600 hover:bg-orange-50"
              data-testid="create-club-small-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Club
            </Button>
          </div>
          
          {clubsLoading ? (
            <Card className="p-4 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" />
            </Card>
          ) : clubs.length === 0 ? (
            <Card className="p-6 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
              <Users2 className="w-10 h-10 text-orange-400 mx-auto mb-2" />
              <p className="text-gray-700 font-medium mb-1">No clubs yet</p>
              <p className="text-sm text-gray-500 mb-3">Create a Run Club to bring your basketball crew into Open Runs</p>
              <Button
                onClick={() => navigate('/create-club')}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600"
              >
                Create Your First Club
              </Button>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clubs.map((club) => (
                <Card
                  key={club.id}
                  className="p-4 cursor-pointer hover:shadow-md transition-all hover:border-orange-300"
                  onClick={() => navigate(`/club/${club.slug}`)}
                  data-testid={`club-card-${club.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{club.name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {club.location_label}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {club.member_count || 1} member{(club.member_count || 1) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Runs List */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Runs</h2>
            
            {runs.upcoming.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No upcoming runs</p>
                <Button 
                  onClick={() => navigate('/create-game')}
                  variant="outline"
                >
                  Create Your First Run
                </Button>
              </Card>
            ) : (
              runs.upcoming.map((game) => {
                const { date, time } = formatDateTime(game.date_time, game.tz);
                const isSelected = selectedGame?.id === game.id;
                
                return (
                  <Card
                    key={game.id}
                    className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                      isSelected ? 'ring-2 ring-orange-500 bg-orange-50' : ''
                    }`}
                    onClick={() => navigate(`/games/${game.id}`)}
                    data-testid={`run-card-${game.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{game.title}</h3>
                          {getStatusBadge(game.display_status, game.player_count, game.min_players)}
                          {game.host_role === 'cohost' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600">
                              Co-hosted
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {time}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {game.location_name}
                          </span>
                        </div>
                        
                        <div className="mt-2 flex items-center gap-4">
                          <span className="text-sm font-medium text-gray-700">
                            <Users className="w-4 h-4 inline mr-1" />
                            {game.player_count} / {game.max_players} players
                          </span>
                          {game.waitlist_count > 0 && (
                            <span className="text-sm text-orange-600">
                              +{game.waitlist_count} on waitlist
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-shrink-0 flex-col items-end gap-2">
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                          isSelected ? 'rotate-90' : ''
                        }`} />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            openPlayerManagement(game);
                          }}
                          data-testid={`list-view-players-${game.id}`}
                        >
                          <Users className="mr-1.5 h-4 w-4" />
                          View Players
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDuplicateGame(game);
                          }}
                          data-testid={`list-duplicate-game-${game.id}`}
                        >
                          <Copy className="mr-1.5 h-4 w-4" />
                          Duplicate Game
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })
            )}

            {runs.past.length > 0 && (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mt-8">Past Runs</h2>
                {runs.past.slice(0, 5).map((game) => {
                  const { date, time } = formatDateTime(game.date_time, game.tz);
                  
                  return (
                    <Card 
                      key={game.id}
                      className="p-4 bg-gray-50 opacity-75 cursor-pointer hover:opacity-90 transition-all"
                      onClick={() => navigate(`/games/${game.id}`)}
                      data-testid={`past-run-card-${game.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-700">{game.title}</h3>
                            {getStatusBadge(game.display_status, game.player_count, game.min_players)}
                          </div>
                          <p className="text-sm text-gray-500">{date} • {game.player_count} players</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {game.check_in?.window?.available && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={(event) => {
                                event.stopPropagation();
                                openPlayerManagement(game);
                              }}
                              className="bg-orange-500 text-white hover:bg-orange-600"
                              data-testid={`past-check-in-${game.id}`}
                            >
                              <UserCheck className="mr-1.5 h-4 w-4" />
                              Check In
                            </Button>
                          )}
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </>
            )}
          </div>

          {/* Selected Game Detail */}
          <div className="lg:col-span-1">
            {selectedGame ? (
              <Card className="p-4 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  {selectedGame.title}
                  {selectedGame.host_role === 'cohost' && (
                    <span className="inline-flex items-center px-2 py-0.5 bg-zinc-100 text-zinc-600 text-xs font-medium rounded-full">
                      Co-hosted
                    </span>
                  )}
                  {selectedGame.is_private && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 text-zinc-600 text-xs font-medium rounded-full">
                      <Lock className="w-3 h-3" />
                      Private
                    </span>
                  )}
                </h3>
                
                {/* Quick Actions */}
                <div className="space-y-2 mb-4">
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate(`/games/${selectedGame.id}`)}
                    >
                      View Game
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate(`/games/${selectedGame.id}/edit`)}
                      disabled={selectedGame.host_role === 'cohost'}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDuplicateGame(selectedGame)}
                      data-testid="duplicate-game-btn"
                    >
                      <Copy className="mr-1 h-4 w-4" />
                      Duplicate
                    </Button>
                    {!isPastRun(selectedGame.date_time)
                      && !['cancelled', 'canceled', 'completed'].includes(String(selectedGame.status || '').toLowerCase()) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleCancelGame(selectedGame)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                  
                  {/* Invite & Share Row */}
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-orange-500 hover:bg-orange-600 text-white flex-1"
                      onClick={() => openInviteModal(selectedGame)}
                      data-testid="invite-players-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Invite Players
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openShareSheet(selectedGame)}
                      className="flex-1"
                      data-testid="share-link-btn"
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Share Game
                    </Button>
                  </div>
                </div>

                {/* Roster */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Roster ({selectedGame.player_count}/{selectedGame.max_players})
                  </h4>
                  {isPastRun(selectedGame.date_time) && (
                    <p className="text-xs text-zinc-500 mb-2" data-testid="host-roster-locked-note">
                      This run has started — the roster is locked.
                    </p>
                  )}
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {[...selectedGame.team_a_players, ...selectedGame.team_b_players].map((player, i) => (
                      <div 
                        key={player.user_id}
                        className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50 text-sm"
                      >
                        <span className="truncate">{player.username}</span>
                        {!isPastRun(selectedGame.date_time) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePlayer(selectedGame.id, player.user_id, player.username);
                            }}
                            data-testid="host-remove-player-btn"
                          >
                            <UserMinus className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {selectedGame.player_count === 0 && (
                      <p className="text-sm text-gray-400 text-center py-2">No players yet</p>
                    )}
                  </div>
                </div>

                {/* Waitlist */}
                {selectedGame.waitlist_enabled && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Waitlist ({waitlist.length})
                    </h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {waitlist.map((entry, i) => (
                        <div 
                          key={entry.user_id}
                          className="flex items-center justify-between py-1.5 px-2 rounded bg-orange-50 text-sm"
                        >
                          <span className="truncate">
                            <span className="text-orange-600 font-medium mr-1">#{i + 1}</span>
                            {entry.username}
                          </span>
                          {!isPastRun(selectedGame.date_time) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePromoteFromWaitlist(selectedGame.id, entry.user_id, entry.username);
                              }}
                              disabled={selectedGame.player_count >= selectedGame.max_players}
                              data-testid="host-promote-player-btn"
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {waitlist.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-2">No one on waitlist</p>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ) : (
              <Card className="p-8 text-center text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a run to manage</p>
              </Card>
            )}
          </div>
        </div>
          </>
        )}
      </div>

      <Footer variant="slim" />

      <HostPlayerManagementDialog
        game={playersGame}
        open={playersOpen}
        onOpenChange={setPlayersOpen}
        onInvitePlayers={(game) => openInviteModal(game)}
        onMessagePlayers={openPlayerMessage}
        onRemovePlayer={handleRemovePlayer}
        onPromoteWaitlist={handlePromoteFromWaitlist}
      />

      <HostPlayerMessageDialog
        game={messageGame}
        open={messageOpen}
        onOpenChange={setMessageOpen}
        onSent={(result) => toast.success(
          `Update queued for ${result.eligible_count} player${result.eligible_count === 1 ? '' : 's'}`
        )}
      />

      <HostGameCancellationDialog
        game={gameToCancel}
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        message={cancelReason}
        onMessageChange={setCancelReason}
        onConfirm={confirmCancelGame}
        isSubmitting={isCancelling}
        dateLabel={gameToCancel ? formatDateTime(gameToCancel.date_time, gameToCancel.tz).date : 'Date TBD'}
        timeLabel={gameToCancel ? formatDateTime(gameToCancel.date_time, gameToCancel.tz).time : ''}
      />

      {/* Invite Players Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-500" />
              Invite Players
            </DialogTitle>
            <DialogDescription>
              Select players to invite to "{selectedGame?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <Input
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
              data-testid="invite-search-input"
            />
            
            {/* Players List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {loadingInvitablePlayers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : filteredPlayers.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {searchQuery ? 'No players match your search' : 'No players available to invite'}
                  </p>
                  <p className="text-xs mt-1">Eligible followers, club members, and players from your past runs appear here</p>
                </div>
              ) : (
                filteredPlayers.map(player => (
                  <div
                    key={player.id}
                    className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-zinc-50 transition-colors"
                    data-testid={`invitable-player-${player.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900 truncate">{invitationPlayerLabel(player)}</p>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        {player.skill_level && <span>{player.skill_level}</span>}
                        {player.is_follower && (
                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">
                            Follower
                          </span>
                        )}
                        {player.is_past_player && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">
                            Past Player
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      disabled={player.eligible === false || player.already_invited || invitingPlayer === player.id}
                      onClick={() => handleInvitePlayer(player.id)}
                      className={player.eligible === false || player.already_invited
                        ? 'bg-zinc-100 text-zinc-500 cursor-not-allowed' 
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                      }
                      data-testid={`invite-btn-${player.id}`}
                    >
                      {invitingPlayer === player.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : player.already_invited ? (
                        'Invited'
                      ) : player.eligible === false ? (
                        'Ineligible'
                      ) : (
                        <>
                          <Send className="w-3 h-3 mr-1" />
                          Invite
                        </>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ShareRunSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        run={shareRun}
        source="host_dashboard"
      />
    </div>
  );
};

export default HostDashboard;
